import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dayjs, { Dayjs } from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import dayOfYear from "dayjs/plugin/dayOfYear";
import isoWeek from "dayjs/plugin/isoWeek";
import isBetween from "dayjs/plugin/isBetween";
import duration from "dayjs/plugin/duration";
import debounce from "lodash.debounce";
import { Coords, ZoomLevel } from "../../types/global";
import { isAvailableZoom } from "../../types/guards";
import { getDatesRange, getParsedDatesRange } from "../../utils/getDatesRange";
import { parseDay } from "../../utils/dates";
import { getCols, getVisibleCols } from "../../utils/getCols";
import {
  buttonAnnualJump,
  buttonBiannualJump,
  buttonMonthJump,
  buttonQuarterlyJump,
  hoursInDay,
  outsideWrapperId,
  zoom2ButtonJump,
} from "../../constants";
import { calendarContext } from "./calendarContext";
import { CalendarProviderProps } from "./types";
import { searchHolidays } from "../../../services/api/holidays/holidaysService";
import { IHoliday } from "../../../@types/holiday.type";
import { IGrid } from "../../../@types/grid.type";
dayjs.extend(weekOfYear);
dayjs.extend(dayOfYear);
dayjs.extend(isoWeek);
dayjs.extend(isBetween);
dayjs.extend(duration);

type Direction = "back" | "forward" | "middle";

const CalendarProvider = ({
  data,
  children,
  isLoading,
  config,
  defaultStartDate = dayjs(),
  onRangeChange,
  onFilterData,
  onClearFilterData,
}: CalendarProviderProps) => {
  const { zoom: configZoom, maxRecordsPerPage = 50 } = config;
  const [zoom, setZoom] = useState<ZoomLevel>(configZoom);
  const [date, setDate] = useState(dayjs(defaultStartDate));
  const [isInitialized, setIsInitialized] = useState(false);
  const [cols, setCols] = useState(getCols(zoom, date));
  const isNextZoom = zoom === 0;
  const isPrevZoom = zoom === 1;
  const range = useMemo(() => getParsedDatesRange(date, zoom), [date, zoom]);
  const startDate = getDatesRange(date, zoom).startDate;
  const dayOfYear = dayjs(startDate).dayOfYear();
  const parsedStartDate = parseDay(startDate);
  const outsideWrapper = useRef<HTMLElement | null>(null);
  const [tilesCoords, setTilesCoords] = useState<Coords[]>([{ x: 0, y: 0 }]);
  const [listHolidays, setListHolidays] = useState<IHoliday[]>([]);

  useEffect(() => {
    const loadHolidays = async () => {
      try {
        const response = (await searchHolidays({
          startDate: dayjs(range.startDate).subtract(1, "day").toISOString(),
          endDate: dayjs(range.endDate).subtract(1, "day").toISOString(),
          skip: 0,
          take: 500,
        })) as IGrid<IHoliday>;

        setListHolidays(response.items);
      } catch (error) {
        console.error("Erro ao buscar feriados:", error);
        setListHolidays([]);
      }
    };

    loadHolidays();
  }, [range.startDate, range.endDate]);

  // const moveHorizontalScroll = useCallback(
  //   (direction: Direction, behavior: ScrollBehavior = "auto") => {
  //     const canvasWidth = getCanvasWidth();
  //     switch (direction) {
  //       case "back":
  //         return outsideWrapper.current?.scrollTo({
  //           behavior,
  //           left: canvasWidth / 3,
  //         });

  //       case "forward":
  //         return outsideWrapper.current?.scrollTo({
  //           behavior,
  //           left: canvasWidth / 3,
  //         });

  //       case "middle": {
  //         const leftOffset = canvasWidth / screenWidthMultiplier / 4; // 1/4 of component's width
  //         return outsideWrapper.current?.scrollTo({
  //           behavior,
  //           left: canvasWidth / 2 - leftOffset,
  //         });
  //       }

  //       default:
  //         return outsideWrapper.current?.scrollTo({
  //           behavior,
  //           left: canvasWidth / 2,
  //         });
  //     }
  //   },
  //   []
  // );

  const updateTilesCoords = (coords: Coords[]) => {
    setTilesCoords(coords);
  };

  const loadMore = useCallback(
    (direction: Direction) => {
      const cols = getVisibleCols(zoom, date);
      let offset: number;
      switch (zoom) {
        case 0:
          offset = cols * 7;
          break;
        case 1:
          offset = cols;
          break;
        case 2:
          offset = Math.ceil(cols / hoursInDay);
          break;
      }
      const load = debounce(() => {
        switch (direction) {
          case "back":
            setDate((prev) => prev.subtract(offset, "days"));
            break;
          case "forward":
            setDate((prev) => prev.add(offset, "days"));
            break;
          case "middle":
            setDate(dayjs());
            break;
        }
        onRangeChange?.(range);
      }, 300);
      load();
    },
    [onRangeChange, range, zoom, date]
  );

  useEffect(() => {
    outsideWrapper.current = document.getElementById(outsideWrapperId);
    setCols(getCols(zoom, date));
  }, [zoom, date]);

  useEffect(() => {
    const handleResize = () => setCols(getCols(zoom, date));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [zoom, date]);

  useEffect(() => {
    onRangeChange?.(range);
  }, [onRangeChange, range]);

  useEffect(() => {
    // when defaultStartDate changes repaint grid
    setIsInitialized(false);
  }, [defaultStartDate]);

  useEffect(() => {
    if (isInitialized) return;

    //moveHorizontalScroll("middle");
    setIsInitialized(true);
    setDate(defaultStartDate);
  }, [defaultStartDate, isInitialized]);
  //}, [defaultStartDate, isInitialized, moveHorizontalScroll]);

  const handleGoNext = () => {
    if (isLoading) return;
    setDate((prev) => {
      switch (zoom) {
        case 0:
          return prev.add(buttonQuarterlyJump, "month");
          break;
        case 1:
          return prev.add(buttonMonthJump, "month");
          break;
        case 2:
          return prev.add(zoom2ButtonJump, "hours");
          break;
        case 3:
          return prev.add(buttonBiannualJump, "month");
          break;
        case 4:
          return prev.add(buttonAnnualJump, "month");
          break;
        default:
          return prev.add(buttonMonthJump, "month");
          break;
      }
    });
    onRangeChange?.(range);
  };

  const handleDatePerMonthAndYear = (newDate: Dayjs) => {
    if (isLoading) return;

    setDate(newDate);
    onRangeChange?.(range);
  };

  const handleScrollNext = useCallback(() => {
    return null;
    // if (isLoading) return;

    //   loadMore("forward");
    //   debounce(() => {
    //     moveHorizontalScroll("forward");
    //   }, 300)();
    // }, [isLoading, loadMore, moveHorizontalScroll]);
  }, [isLoading, loadMore]);

  const handleGoPrev = () => {
    if (isLoading) return;
    setDate((prev) => {
      switch (zoom) {
        case 0:
          return prev.subtract(buttonQuarterlyJump, "month");
          break;
        case 1:
          return prev.subtract(buttonMonthJump, "month");
          break;
        case 2:
          return prev.subtract(zoom2ButtonJump, "hours");
          break;
        case 3:
          return prev.subtract(buttonBiannualJump, "month");
          break;
        case 4:
          return prev.subtract(buttonAnnualJump, "month");
          break;
        default:
          return prev.subtract(buttonMonthJump, "month");
          break;
      }
    });
    onRangeChange?.(range);
  };

  const handleScrollPrev = useCallback(() => {
    return null;
    //   if (!isInitialized || isLoading) return;
    //   loadMore("back");
    //   debounce(() => {
    //     moveHorizontalScroll("back");
    //   }, 300)();
    // }, [isInitialized, isLoading, loadMore, moveHorizontalScroll]);
  }, [isInitialized, isLoading, loadMore]);

  const handleGoToday = useCallback(() => {
    // if (isLoading) return;
    // loadMore("middle");
    //   debounce(() => {
    //     moveHorizontalScroll("middle", "smooth");
    //   }, 300)();
    // }, [isLoading, loadMore, moveHorizontalScroll]);
  }, [isLoading, loadMore]);

  const zoomIn = () => changeZoom(1);
  const zoomOut = () => changeZoom(0);
  const zoomBiannual = () => changeZoom(3);
  const zoomAnnual = () => changeZoom(4);

  const changeZoom = (zoomLevel: number) => {
    if (!isAvailableZoom(zoomLevel)) return;
    setZoom(zoomLevel);
    setCols(getCols(zoomLevel, date));
    onRangeChange?.(range);
  };

  const handleFilterData = () => onFilterData?.();

  const { Provider } = calendarContext;

  return (
    <Provider
      value={{
        data,
        config,
        handleGoNext,
        handleScrollNext,
        handleGoPrev,
        handleScrollPrev,
        handleGoToday,
        zoomIn,
        zoomOut,
        zoomBiannual,
        zoomAnnual,
        zoom,
        isNextZoom,
        isPrevZoom,
        date,
        isLoading,
        cols,
        startDate: parsedStartDate,
        dayOfYear,
        handleFilterData,
        tilesCoords,
        updateTilesCoords,
        recordsThreshold: maxRecordsPerPage,
        onClearFilterData,
        handleDatePerMonthAndYear,
        range,
        listHolidays,
      }}
    >
      {children}
    </Provider>
  );
};

const useCalendar = () => useContext(calendarContext);

export default CalendarProvider;
export { useCalendar };
